import { type ReactNode, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  Calendar,
  Users,
  AlertTriangle,
  Upload,
  Boxes,
  QrCode,
  Settings,
  Pause,
  Play,
} from 'lucide-react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useSchedulerStatus, useSchedulerPause } from '../hooks/useSchedulerPause';
import { ConnectionStatus } from './ConnectionStatus';

interface NavItem {
  label: string;
  href: string;
  icon: typeof LayoutDashboard;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard },
  { label: 'Orders', href: '/orders', icon: ClipboardList },
  { label: 'Schedule', href: '/schedule', icon: Calendar },
  { label: 'People', href: '/people', icon: Users },
  { label: 'Alerts', href: '/alerts', icon: AlertTriangle },
  { label: 'Imports', href: '/imports', icon: Upload },
  { label: 'Inventory', href: '/inventory', icon: Boxes },
  { label: 'Serialized', href: '/serialized', icon: QrCode },
  { label: 'Settings', href: '/settings', icon: Settings },
];

function NavLink({ item }: { item: NavItem }) {
  const location = useLocation();
  const isActive = location.pathname === item.href;
  const Icon = item.icon;

  return (
    <Link
      to={item.href}
      className={`
        flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors
        ${
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-foreground-muted hover:bg-background-subtle hover:text-foreground'
        }
      `}
    >
      <Icon className="h-4 w-4" />
      {item.label}
    </Link>
  );
}

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  // Subscribe to schedule channel for real-time updates
  const channels = useMemo(() => ['schedule'], []);
  const { connectionState, lastConnectedAt, reconnect } = useWebSocket(channels);

  // Scheduler pause/resume state
  const { data: schedulerStatus } = useSchedulerStatus();
  const pauseMutation = useSchedulerPause();
  const isPaused = schedulerStatus?.isPaused ?? false;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="h-14 border-b border-border px-4 flex items-center justify-between bg-background sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <span className="text-sm font-bold text-primary-foreground">S</span>
            </div>
            <span className="text-lg font-semibold text-foreground">
              Scheduler
            </span>
          </Link>
          <span className="text-xs font-medium text-foreground-muted bg-background-subtle px-2 py-1 rounded">
            Planner
          </span>
        </div>

        <div className="flex items-center gap-4">
          <ConnectionStatus
            connectionState={connectionState}
            lastConnectedAt={lastConnectedAt}
            onReconnect={reconnect}
          />
          <button
            onClick={() => pauseMutation.mutate(!isPaused)}
            disabled={pauseMutation.isPending}
            className={`
              flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors
              ${isPaused
                ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                : 'hover:bg-background-subtle text-foreground-muted'
              }
            `}
            title={isPaused
              ? `Paused by ${schedulerStatus?.pausedBy ?? 'unknown'} — ${schedulerStatus?.pendingTriggers ?? 0} pending changes`
              : 'Pause schedule recalculation'
            }
          >
            {isPaused ? (
              <>
                <Play className="h-3.5 w-3.5" />
                <span>Resume</span>
                {(schedulerStatus?.pendingTriggers ?? 0) > 0 && (
                  <span className="bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-full text-[10px] font-semibold">
                    {schedulerStatus?.pendingTriggers}
                  </span>
                )}
              </>
            ) : (
              <>
                <Pause className="h-3.5 w-3.5" />
                <span>Pause</span>
              </>
            )}
          </button>
          {pauseMutation.isError && (
            <span className="text-xs text-red-600">
              Failed to {isPaused ? 'resume' : 'pause'}
            </span>
          )}
          <Link
            to="/settings"
            className="p-2 rounded-md hover:bg-background-subtle transition-colors"
            aria-label="Settings"
          >
            <Settings className="h-5 w-5 text-foreground-muted" />
          </Link>
          <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
            <span className="text-sm font-medium text-primary-foreground">P</span>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-64 border-r border-border p-4 hidden lg:block bg-background">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink key={item.href} item={item} />
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main id="main-content" className="flex-1 min-w-0 p-6 bg-background-subtle">
          {children}
        </main>
      </div>
    </div>
  );
}
